import { registerBrick, BrickContext, UIBrick } from 'olympe'
import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import { BCPDataGrid } from './BCPDataGrid.jsx'
import { BCPDataGridTextFieldDialog } from './BCPDataGridTextFieldDialog.jsx'
import {BCPDataGridDocumentDialog} from './BCPDataGridDocumentDialog.jsx';
import { BCPDataGridMenu } from './BCPDataGridMenu.jsx'
import PropTypes from 'prop-types'
import {isMaterialEligibleForRF, reasonCodes} from './_enums';
import {combineProps, createEvents} from '../shared/_helpers';

/**
 * Brick that display a data grid showing BCP
 */
export default class BCP extends UIBrick {
    /**
     * This method runs when the brick is ready in the HTML DOM.
     * @override
     * @param {!BrickContext} context
     * @param {!Element} elementDom
     */
    draw(context, elementDom) {
        const events = createEvents([
            {
                name: 'fetchData',
                event: context.getEvent('Fetch Data'),
                before: (event, values) => {
                    context.set('Data', values);
                },
            },
            {
                name: 'multipleEdit',
                event: context.getEvent('Multiple Edit'),
                before: (event, values) => {
                    context.set('Selected Values', values);
                },
            },
            {
                name: 'throwError',
                event: context.getEvent('Throw Error'),
                before: (event, values) => {
                    context.set('Error Message', values);
                },
            },
        ]);

        const reload = context.getProperty('Reload')
        const setReload = value => {
            context.getProperty('Reload').set(value)
        }

        combineProps([
            { name: 'searchText', prop: context.getProperty('Search Text'), defaultValue: '' },
            { name: 'filters', prop: context.getProperty('Filters'), defaultValue: '{}' },
        ]).subscribe(
            props => {
                let parsedFilters;
                try {
                    parsedFilters = JSON.parse(props.filters);
                } catch (e) {
                    console.error(`Error when parsing the filters: ${e}`);
                    parsedFilters = {};
                }
                ReactDOM.render(
                    <BCP.Component
                        {...props}
                        {...events}
                        context={context}
                        reload={reload}
                        filters={parsedFilters}
                        setReload={setReload}
                    />,
                    elementDom
                )
            }
        )
    }
}

/**
 * @param {Object} props
 * @param {Object} props.reload
 * @param {!Object} props.filters
 * @param {string} props.searchText
 * @param {function(*)} props.setReload
 * @param {function(Event, [])} props.multipleEdit
 * @param {function(Event, string)} props.throwError
 * @return {JSX.Element[]}
 * @constructor
 */
BCP.Component = props => {
    const expiryMenuOptions = ['6 months', '1 year']
    const [rows, setRows] = useState([])
    const [rowClicked, setRowClicked] = useState(0)
    const [currentDescription, setCurrentDescription] = useState('')
    const [openDescription, setOpenDescription] = useState(false)
    const [currentDocument, setCurrentDocument] = useState(null)
    const [openDocument, setOpenDocument] = useState(false)
    const [reasonCodeOptions, setReasonCodeOptions] = useState([])

    const handleCloseDescriptionPopup = value => {
        if (typeof value === 'string' && (value || value === '')) {
            rows[rowClicked]['comments'] = value
        }
        setOpenDescription(false)
    }

    const handleCloseDocumentPopup = value => {
        if (value instanceof File) {
            rows[rowClicked]['newUploadedFile'] = value;
        }
        setOpenDocument(false);
    }

    const handleClickOnReasonCodeMenu = item => {
        rows[rowClicked]['reasonCode'] = item.id
    }

    const handleClickOnExpiryMenu = item => {
        const parsedDate = new Date()

        if (item === '6 months') {
            rows[rowClicked]['expiry'] = new Date(parsedDate.setMonth(parsedDate.getMonth() + 6)).toISOString()
        } else {
            rows[rowClicked]['expiry'] = new Date(parsedDate.setMonth(parsedDate.getMonth() + 12)).toISOString()
        }
    }

    const [anchorElReasonCode, setAnchorElReasonCode] = useState(null)

    const handleClickReasonCode = (event, row) => {
        let orderedReasonCodes;
        if(isMaterialEligibleForRF(row)) {
            orderedReasonCodes = [reasonCodes['UM'], reasonCodes['BD'], reasonCodes['RF'], reasonCodes['AM']];
        } else {
            orderedReasonCodes = [reasonCodes['UM'], reasonCodes['BD'], reasonCodes['AM']];
        }
        setReasonCodeOptions(orderedReasonCodes);
        setAnchorElReasonCode(event.currentTarget)
    }

    const [anchorElExpiry, setAnchorElExpiry] = useState(null)

    const handleClickExpiry = event => {
        setAnchorElExpiry(event.currentTarget)
    }

    return [
        <BCPDataGrid
            key={'dataGrid'}
            filters={props.filters}
            reload={props.reload}
            setReload={props.setReload}
            multipleEdit={props.multipleEdit}
            throwError={props.throwError}
            searchText={props.searchText}
            rows={rows}
            setRows={setRows}
            setRowClicked={setRowClicked}
            setOpenDescription={setOpenDescription}
            setCurrentDescription={setCurrentDescription}
            setOpenDocument={setOpenDocument}
            setCurrentDocument={setCurrentDocument}
            handleClickReasonCode={handleClickReasonCode}
            handleClickExpiry={handleClickExpiry}
        />,
        <BCPDataGridMenu
            key={'bcpDataGridMenuReasonCode'}
            id={'bcp-reason-code'}
            color={'primary'}
            options={reasonCodeOptions}
            onClick={handleClickOnReasonCodeMenu}
            anchorEl={anchorElReasonCode}
            setAnchorEl={setAnchorElReasonCode}
        />,
        <BCPDataGridMenu
            key={'bcpDataGridMenuExpiry'}
            id={'bcp-expiry-menu'}
            color={'primary'}
            options={expiryMenuOptions}
            onClick={handleClickOnExpiryMenu}
            anchorEl={anchorElExpiry}
            setAnchorEl={setAnchorElExpiry}
        />,
        <BCPDataGridTextFieldDialog
            key={'bcpDataGridDialogDescription'}
            title={'Description'}
            rows={4}
            defaultValue={currentDescription}
            variant={'outlined'}
            handleClose={handleCloseDescriptionPopup}
            open={openDescription}
            ariaLabelledby={'edit-description'}
        />,
        <BCPDataGridDocumentDialog
            key={'bcpDataGridDialogDocument'}
            document={currentDocument}
            handleClose={handleCloseDocumentPopup}
            open={openDocument}
        />,
    ]
}

BCP.Component.propTypes = {
    searchText: PropTypes.string,
    reload: PropTypes.object,
    filters: PropTypes.object,
    setReload: PropTypes.func,
    multipleEdit: PropTypes.func,
    throwError: PropTypes.func,
}

registerBrick('017a77022b0ae4e97b93', BCP)
